import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { io } from 'socket.io-client';

const API = '';
const SIGNAL = 'http://43.202.161.149:5001';

const STYLES = [
  { value: 'formal', label: '격식체', desc: '공식 회의록 형식' },
  { value: 'simple', label: '간결체', desc: '핵심만 정리' },
  { value: 'detailed', label: '상세체', desc: '논의 과정 포함' },
  { value: 'action', label: '액션 중심', desc: '할 일 위주' },
];

const QUICK_TEMPLATES = [
  { label: '정기 회의', agenda: '- 지난 회의 Action Items 점검\n- 금주 업무 보고\n- 이슈 및 리스크 공유\n- 차주 계획' },
  { label: '프로젝트 킥오프', agenda: '- 프로젝트 개요 및 목표\n- 범위 및 일정\n- 역할 및 책임\n- 커뮤니케이션 계획' },
  { label: '의사결정 회의', agenda: '- 안건 배경 설명\n- 대안 검토\n- 장단점 분석\n- 최종 결정' },
  { label: '브레인스토밍', agenda: '- 주제 소개\n- 자유 아이디어 발산\n- 아이디어 분류 및 평가\n- 우선순위 선정' },
  { label: '이슈 해결', agenda: '- 이슈 현황 설명\n- 원인 분석\n- 해결 방안 논의\n- 담당자 및 일정 확정' },
  { label: '고객 미팅', agenda: '- 인사 및 소개\n- 요구사항 확인\n- 제안 내용 설명\n- Q&A 및 후속 일정' },
];

const TC = '#00695c';
const TB = '#e0f2f1';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

/* ─── 화상회의 컴포넌트 ─── */
function VideoConference({ onEndCall }) {
  const [phase, setPhase] = useState('lobby'); // lobby, inCall
  const [userName, setUserName] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState('');
  const [participants, setParticipants] = useState([]);
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [callTime, setCallTime] = useState(0);

  // 음성 인식
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimText, setInterimText] = useState('');
  const transcriptRef = useRef('');
  const recognitionRef = useRef(null);

  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const peersRef = useRef({});
  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef({});
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (phase === 'inCall') {
      timerRef.current = setInterval(() => setCallTime(t => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return h > 0 ? `${h}:${m}:${sec}` : `${m}:${sec}`;
  };

  const cleanup = () => {
    stopSpeechRecognition();
    if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    Object.values(peersRef.current).forEach(pc => pc.close());
    peersRef.current = {};
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(t => t.stop()); localStreamRef.current = null; }
    if (screenStreamRef.current) { screenStreamRef.current.getTracks().forEach(t => t.stop()); screenStreamRef.current = null; }
    clearInterval(timerRef.current);
  };

  const startMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      return true;
    } catch (e) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
        localStreamRef.current = stream;
        setVideoOn(false);
        return true;
      } catch (e2) {
        setError('카메라/마이크 접근이 차단되었습니다. 브라우저 설정을 확인해주세요.');
        return false;
      }
    }
  };

  const createPeerConnection = (peerId, peerName) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    peersRef.current[peerId] = pc;

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        socketRef.current.emit('ice_candidate', { target: peerId, candidate: e.candidate });
      }
    };

    pc.ontrack = (e) => {
      const el = remoteVideosRef.current[peerId];
      if (el && e.streams[0]) el.srcObject = e.streams[0];
      setParticipants(prev => {
        const exists = prev.find(p => p.id === peerId);
        if (exists) return prev;
        return [...prev, { id: peerId, name: peerName || '참여자', audioOn: true, videoOn: true }];
      });
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        removePeer(peerId);
      }
    };

    return pc;
  };

  const removePeer = (peerId) => {
    if (peersRef.current[peerId]) {
      peersRef.current[peerId].close();
      delete peersRef.current[peerId];
    }
    setParticipants(prev => prev.filter(p => p.id !== peerId));
  };

  const connectSignaling = () => {
    const socket = io(SIGNAL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('user_joined', async ({ id, name }) => {
      const pc = createPeerConnection(id, name);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('offer', { target: id, sdp: pc.localDescription });
      setParticipants(prev => [...prev.filter(p => p.id !== id), { id, name, audioOn: true, videoOn: true }]);
    });

    socket.on('offer', async ({ from, name, sdp }) => {
      const pc = createPeerConnection(from, name);
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('answer', { target: from, sdp: pc.localDescription });
    });

    socket.on('answer', async ({ from, sdp }) => {
      const pc = peersRef.current[from];
      if (pc) await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    });

    socket.on('ice_candidate', async ({ from, candidate }) => {
      const pc = peersRef.current[from];
      if (pc) {
        try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch(e) {}
      }
    });

    socket.on('user_left', ({ id }) => removePeer(id));

    socket.on('media_toggled', ({ id, type, enabled }) => {
      setParticipants(prev => prev.map(p =>
        p.id === id ? { ...p, [type === 'audio' ? 'audioOn' : 'videoOn']: enabled } : p
      ));
    });

    socket.on('chat_message', (msg) => {
      setChatMessages(prev => [...prev, msg]);
    });

    return socket;
  };

  const handleCreate = async () => {
    if (!userName.trim()) return setError('이름을 입력해주세요.');
    setError('');
    const ok = await startMedia();
    if (!ok) return;
    const socket = connectSignaling();
    socket.emit('create_room', { name: userName }, ({ code }) => {
      setRoomCode(code);
      socket.emit('join_room', { code, name: userName }, () => {});
      setPhase('inCall');
      setCallTime(0);
    });
  };

  const handleJoin = async () => {
    if (!userName.trim()) return setError('이름을 입력해주세요.');
    if (!joinCode.trim()) return setError('회의 코드를 입력해주세요.');
    setError('');
    const ok = await startMedia();
    if (!ok) return;
    const socket = connectSignaling();
    socket.emit('join_room', { code: joinCode.toUpperCase(), name: userName }, ({ error: err, participants: peers }) => {
      if (err) { setError(err === 'Room not found' ? '존재하지 않는 회의입니다.' : err); cleanup(); return; }
      setRoomCode(joinCode.toUpperCase());
      if (peers) setParticipants(peers.map(p => ({ ...p, audioOn: true, videoOn: true })));
      setPhase('inCall');
      setCallTime(0);
    });
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setAudioOn(track.enabled);
        socketRef.current?.emit('toggle_media', { type: 'audio', enabled: track.enabled });
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setVideoOn(track.enabled);
        socketRef.current?.emit('toggle_media', { type: 'video', enabled: track.enabled });
      }
    }
  };

  const toggleScreen = async () => {
    if (screenSharing) {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }
      const videoTrack = localStreamRef.current?.getVideoTracks()[0];
      if (videoTrack) {
        Object.values(peersRef.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) sender.replaceTrack(videoTrack);
        });
      }
      setScreenSharing(false);
    } else {
      try {
        const screen = await navigator.mediaDevices.getDisplayMedia({ video: true });
        screenStreamRef.current = screen;
        const screenTrack = screen.getVideoTracks()[0];
        Object.values(peersRef.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) sender.replaceTrack(screenTrack);
        });
        screenTrack.onended = () => toggleScreen();
        setScreenSharing(true);
      } catch(e) {}
    }
  };

  const sendChat = () => {
    if (!chatInput.trim()) return;
    socketRef.current?.emit('chat_message', { text: chatInput });
    setChatInput('');
  };

  // 음성 인식
  const startSpeechRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Chrome 브라우저에서 음성 인식을 사용할 수 있습니다.'); return; }
    const r = new SR();
    r.lang = 'ko-KR'; r.continuous = true; r.interimResults = true;
    transcriptRef.current = transcript;

    r.onresult = (e) => {
      let interim = '', final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript + ' ';
        else interim += e.results[i][0].transcript;
      }
      if (final) {
        transcriptRef.current += final;
        setTranscript(transcriptRef.current);
      }
      setInterimText(interim);
    };
    r.onend = () => { if (recognitionRef.current) try { r.start(); } catch(e) {} };
    r.onerror = (e) => { if (e.error === 'not-allowed') { alert('마이크 접근이 차단되었습니다.'); stopSpeechRecognition(); } };
    recognitionRef.current = r;
    r.start();
    setIsRecording(true);
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) { recognitionRef.current.onend = null; recognitionRef.current.stop(); recognitionRef.current = null; }
    setIsRecording(false); setInterimText('');
  };

  const handleEndCall = () => {
    const capturedTranscript = transcriptRef.current || transcript;
    cleanup();
    setPhase('lobby');
    setParticipants([]);
    setChatMessages([]);
    setCallTime(0);
    setRoomCode('');
    if (capturedTranscript.trim()) {
      onEndCall(capturedTranscript);
    }
  };

  // === 로비 화면 ===
  if (phase === 'lobby') {
    return (
      <div style={{ maxWidth: 600, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ background: 'white', borderRadius: 16, padding: 32, border: '1px solid #e0e0e0', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: TB, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="4" width="20" height="14" rx="3" stroke={TC} strokeWidth="2"/><path d="M22 8l-4 3v2l4 3V8z" fill={TC} opacity="0.3"/><circle cx="12" cy="11" r="3" stroke={TC} strokeWidth="1.5"/>
              </svg>
            </div>
            <h2 style={{ margin: 0, fontSize: 22, color: '#1a1a2e' }}>화상 회의</h2>
            <p style={{ color: '#888', fontSize: 14, marginTop: 6 }}>새 회의를 시작하거나, 코드로 참여하세요</p>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6, display: 'block' }}>참여자 이름</label>
            <input value={userName} onChange={e => setUserName(e.target.value)} placeholder="이름을 입력하세요"
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #ddd', fontSize: 15, boxSizing: 'border-box' }} />
          </div>

          {error && <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <button onClick={handleCreate} style={{
              padding: '14px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: `linear-gradient(135deg, ${TC}, #00897b)`, color: 'white',
              fontSize: 15, fontWeight: 700, boxShadow: '0 4px 12px rgba(0,105,92,0.3)',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{verticalAlign:'middle',marginRight:6}}>
                <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
              새 회의 시작
            </button>
            <div style={{ position: 'relative' }}>
              <input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} placeholder="회의 코드"
                style={{ width: '100%', padding: '14px 80px 14px 14px', borderRadius: 10, border: '2px solid #e0e0e0', fontSize: 15, fontWeight: 600, letterSpacing: 2, textAlign: 'center', boxSizing: 'border-box' }}
                onKeyDown={e => e.key === 'Enter' && handleJoin()} maxLength={6} />
              <button onClick={handleJoin} style={{
                position: 'absolute', right: 4, top: 4, bottom: 4, padding: '0 16px',
                borderRadius: 8, border: 'none', background: TC, color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}>참여</button>
            </div>
          </div>

          <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 13, color: '#666', lineHeight: 1.8 }}>
              <strong style={{ color: '#333' }}>사용법:</strong><br/>
              1. "새 회의 시작"으로 회의를 만들면 회의 코드가 생성됩니다<br/>
              2. 다른 참여자에게 코드를 공유하세요<br/>
              3. 회의 중 음성 인식으로 내용을 자동 기록할 수 있습니다<br/>
              4. 회의 종료 시 기록된 내용으로 회의록을 자동 생성합니다
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === 화상 회의 화면 ===
  const allPeers = participants;
  const totalVideos = 1 + allPeers.length;
  const gridCols = totalVideos <= 1 ? 1 : totalVideos <= 4 ? 2 : totalVideos <= 6 ? 3 : 4;

  return (
    <div style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', background: '#1a1a2e' }}>
      {/* 상단 바 */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', background: 'rgba(0,0,0,0.3)', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4caf50', animation: 'pulse2 2s infinite' }}/>
          <span style={{ color: 'white', fontSize: 14, fontWeight: 600 }}>{formatTime(callTime)}</span>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.1)', padding: '4px 14px', borderRadius: 6 }}>
          <span style={{ color: '#aaa', fontSize: 12 }}>회의 코드: </span>
          <span style={{ color: '#ffd54f', fontSize: 15, fontWeight: 700, letterSpacing: 2 }}>{roomCode}</span>
          <button onClick={() => { navigator.clipboard.writeText(roomCode); }} style={{
            marginLeft: 8, background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 12,
          }}>복사</button>
        </div>
        <span style={{ color: '#aaa', fontSize: 13 }}>{totalVideos}명 참여</span>
        <div style={{ flex: 1 }}/>

        {/* 음성 인식 토글 */}
        <button onClick={isRecording ? stopSpeechRecognition : startSpeechRecognition} style={{
          padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
          background: isRecording ? '#ef5350' : 'rgba(255,255,255,0.15)', color: 'white',
          display: 'flex', alignItems: 'center', gap: 6,
          animation: isRecording ? 'pulse2 1.5s infinite' : 'none',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="9" y="2" width="6" height="12" rx="3" fill="white"/><path d="M5 11a7 7 0 0014 0" stroke="white" strokeWidth="2"/>
          </svg>
          {isRecording ? '기록 중...' : '회의 기록'}
        </button>
      </div>

      {/* 비디오 그리드 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{
          flex: 1, display: 'grid',
          gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
          gap: 8, padding: 8,
          alignContent: 'center',
        }}>
          {/* 내 비디오 */}
          <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#2a2a3e', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video ref={localVideoRef} autoPlay muted playsInline style={{
              width: '100%', height: '100%', objectFit: 'cover',
              display: videoOn ? 'block' : 'none', transform: 'scaleX(-1)',
            }}/>
            {!videoOn && (
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: TC, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: 'white', fontWeight: 700 }}>
                {userName[0] || 'M'}
              </div>
            )}
            <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.6)', padding: '4px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'white', fontSize: 12 }}>{userName} (나)</span>
              {!audioOn && <svg width="14" height="14" viewBox="0 0 24 24"><path d="M2 2l20 20M9 5v7l4 4M5 11a7 7 0 0012 5" stroke="#ef5350" strokeWidth="2"/></svg>}
            </div>
          </div>

          {/* 상대방 비디오 */}
          {allPeers.map(p => (
            <div key={p.id} style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#2a2a3e', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <video ref={el => { if (el) remoteVideosRef.current[p.id] = el; }} autoPlay playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: p.videoOn !== false ? 'block' : 'none' }}/>
              {p.videoOn === false && (
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#546e7a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: 'white', fontWeight: 700 }}>
                  {(p.name || '?')[0]}
                </div>
              )}
              <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.6)', padding: '4px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: 'white', fontSize: 12 }}>{p.name}</span>
                {p.audioOn === false && <svg width="14" height="14" viewBox="0 0 24 24"><path d="M2 2l20 20M9 5v7l4 4M5 11a7 7 0 0012 5" stroke="#ef5350" strokeWidth="2"/></svg>}
              </div>
            </div>
          ))}
        </div>

        {/* 채팅 + 음성 기록 사이드바 */}
        {chatOpen && (
          <div style={{ width: 320, background: '#16213e', display: 'flex', flexDirection: 'column', borderLeft: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: 8 }}>
              <button onClick={() => setChatOpen(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 18 }}>x</button>
            </div>

            {/* 음성 기록 */}
            {(transcript || interimText) && (
              <div style={{ padding: 12, borderBottom: '1px solid rgba(255,255,255,0.1)', maxHeight: 200, overflowY: 'auto' }}>
                <div style={{ fontSize: 11, color: '#ffd54f', fontWeight: 600, marginBottom: 6 }}>회의 기록 (음성 인식)</div>
                <div style={{ fontSize: 13, color: '#ccc', lineHeight: 1.6 }}>
                  {transcript}
                  {interimText && <span style={{ color: '#ffd54f', fontStyle: 'italic' }}>{interimText}</span>}
                </div>
              </div>
            )}

            {/* 채팅 메시지 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              {chatMessages.map((m, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#64b5f6' }}>{m.name}</span>
                  <div style={{ fontSize: 13, color: '#ddd', marginTop: 2, lineHeight: 1.5 }}>{m.text}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: 10, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: 6 }}>
              <input value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="메시지 입력..."
                onKeyDown={e => e.key === 'Enter' && sendChat()}
                style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', fontSize: 13 }}/>
              <button onClick={sendChat} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: TC, color: 'white', cursor: 'pointer', fontSize: 13 }}>전송</button>
            </div>
          </div>
        )}
      </div>

      {/* 하단 컨트롤 바 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        padding: '12px 20px', background: 'rgba(0,0,0,0.4)',
      }}>
        <button onClick={toggleAudio} style={{
          width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: audioOn ? 'rgba(255,255,255,0.15)' : '#ef5350',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} title={audioOn ? '마이크 끄기' : '마이크 켜기'}>
          {audioOn ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="9" y="2" width="6" height="12" rx="3" fill="white"/><path d="M5 11a7 7 0 0014 0" stroke="white" strokeWidth="2" strokeLinecap="round"/><path d="M12 18v4M9 22h6" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="9" y="2" width="6" height="12" rx="3" fill="white" opacity="0.5"/><path d="M3 3l18 18" stroke="white" strokeWidth="2.5" strokeLinecap="round"/></svg>
          )}
        </button>

        <button onClick={toggleVideo} style={{
          width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: videoOn ? 'rgba(255,255,255,0.15)' : '#ef5350',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} title={videoOn ? '카메라 끄기' : '카메라 켜기'}>
          {videoOn ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="15" height="14" rx="3" fill="white"/><path d="M17 9l5-3v12l-5-3V9z" fill="white"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="15" height="14" rx="3" fill="white" opacity="0.5"/><path d="M3 3l18 18" stroke="white" strokeWidth="2.5" strokeLinecap="round"/></svg>
          )}
        </button>

        <button onClick={toggleScreen} style={{
          width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: screenSharing ? '#4caf50' : 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} title="화면 공유">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="3" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/><path d="M8 21h8M12 17v4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            {screenSharing && <path d="M8 10l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>}
          </svg>
        </button>

        <button onClick={() => setChatOpen(!chatOpen)} style={{
          width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: chatOpen ? '#1565c0' : 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} title="채팅">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="white" strokeWidth="2"/>
          </svg>
        </button>

        <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.2)', margin: '0 8px' }}/>

        <button onClick={handleEndCall} style={{
          padding: '12px 28px', borderRadius: 24, border: 'none', cursor: 'pointer',
          background: '#ef5350', color: 'white', fontSize: 14, fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="white" strokeWidth="2"/>
            <path d="M3 3l18 18" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          회의 종료
        </button>
      </div>

      <style>{`@keyframes pulse2 { 0%,100% { opacity:1; } 50% { opacity:0.6; } }`}</style>
    </div>
  );
}

/* ─── 메인 MeetingAgent ─── */
function MeetingAgent() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('create');
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState('');
  const [agenda, setAgenda] = useState('');
  const [notes, setNotes] = useState('');
  const [style, setStyle] = useState('formal');
  const [rawText, setRawText] = useState('');
  const [result, setResult] = useState('');
  const [refineInput, setRefineInput] = useState('');
  const [savedList, setSavedList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 녹음 상태
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [interimText, setInterimText] = useState('');
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (mode === 'history') loadList();
  }, [mode]);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('meetingAgentGuideDismissed');
    if (!dismissed) setShowGuide(true);
  }, []);

  const dismissGuide = () => {
    setShowGuide(false);
    sessionStorage.setItem('meetingAgentGuideDismissed', 'true');
  };

  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => setRecordTime(t => t + 1), 1000);
    } else { clearInterval(timerRef.current); }
    return () => clearInterval(timerRef.current);
  }, [isRecording, isPaused]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const startRecording = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Chrome 브라우저에서 음성 인식을 사용할 수 있습니다.'); return; }
    const r = new SR(); r.lang = 'ko-KR'; r.continuous = true; r.interimResults = true;
    finalTranscriptRef.current = mode === 'create' ? notes : rawText;
    r.onresult = (e) => {
      let interim = '', final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript + ' ';
        else interim += e.results[i][0].transcript;
      }
      if (final) { finalTranscriptRef.current += final; mode === 'create' ? setNotes(finalTranscriptRef.current) : setRawText(finalTranscriptRef.current); }
      setInterimText(interim);
    };
    r.onend = () => { if (recognitionRef.current && isRecording) try { r.start(); } catch(e) {} };
    r.onerror = (e) => { if (e.error === 'not-allowed') { alert('마이크 접근이 차단되었습니다.'); stopRecording(); } };
    recognitionRef.current = r; r.start();
    setIsRecording(true); setIsPaused(false); setRecordTime(0); setInterimText('');
  }, [mode, notes, rawText, isRecording]);

  const pauseRecording = () => { if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; } setIsPaused(true); setInterimText(''); };

  const resumeRecording = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition; if (!SR) return;
    const r = new SR(); r.lang = 'ko-KR'; r.continuous = true; r.interimResults = true;
    finalTranscriptRef.current = mode === 'create' ? notes : rawText;
    r.onresult = (e) => {
      let interim = '', final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) { if (e.results[i].isFinal) final += e.results[i][0].transcript + ' '; else interim += e.results[i][0].transcript; }
      if (final) { finalTranscriptRef.current += final; mode === 'create' ? setNotes(finalTranscriptRef.current) : setRawText(finalTranscriptRef.current); }
      setInterimText(interim);
    };
    r.onend = () => { if (recognitionRef.current && !isPaused) try { r.start(); } catch(e) {} };
    recognitionRef.current = r; r.start(); setIsPaused(false);
  };

  const stopRecording = () => {
    if (recognitionRef.current) { recognitionRef.current.onend = null; recognitionRef.current.stop(); recognitionRef.current = null; }
    setIsRecording(false); setIsPaused(false); setInterimText('');
  };

  const loadList = async () => { try { const res = await fetch(`${API}/meeting/list`); setSavedList(await res.json()); } catch(e) {} };

  const handleGenerate = async () => {
    if (mode === 'create' && !agenda.trim() && !notes.trim()) return alert('안건 또는 회의 내용을 입력해주세요.');
    if (mode === 'audio_text' && !rawText.trim()) return alert('회의 텍스트를 입력해주세요.');
    if (isRecording) stopRecording();
    setLoading(true);
    try {
      const body = mode === 'create'
        ? { mode: 'create', title, meeting_date: meetingDate, location, attendees, agenda, notes, style }
        : { mode: 'audio_text', raw_text: rawText, title, meeting_date: meetingDate, attendees };
      const res = await fetch(`${API}/meeting/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      setResult(data.minutes || data.error || '생성 실패');
    } catch(e) { setResult('생성 실패: ' + e.message); }
    setLoading(false);
  };

  const handleRefine = async () => {
    if (!refineInput.trim()) return; setLoading(true);
    try {
      const res = await fetch(`${API}/meeting/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode: 'refine', original: result, instruction: refineInput }) });
      const data = await res.json(); setResult(data.minutes || data.error || '수정 실패'); setRefineInput('');
    } catch(e) { setResult('수정 실패'); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!result) return;
    try {
      const res = await fetch(`${API}/meeting/save`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: title || '무제 회의록', meeting_date: meetingDate, location, attendees, content: result }) });
      const data = await res.json(); alert(data.message || '저장 완료');
    } catch(e) { alert('저장 실패'); }
  };

  const handleLoad = async (id) => {
    try {
      const res = await fetch(`${API}/meeting/${id}`); const data = await res.json();
      setTitle(data.title || ''); setMeetingDate(data.meeting_date || ''); setLocation(data.location || '');
      setAttendees(data.attendees || ''); setResult(data.content || ''); setSelectedId(id); setMode('create');
    } catch(e) { alert('불러오기 실패'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try { await fetch(`${API}/meeting/${id}`, { method: 'DELETE' }); loadList(); if (selectedId === id) { setSelectedId(null); setResult(''); } } catch(e) { alert('삭제 실패'); }
  };

  const handleCopy = () => { navigator.clipboard.writeText(result); alert('복사되었습니다.'); };

  const handleDownload = async (format) => {
    try {
      const res = await fetch(`${API}/meeting/download/${format}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ content: result, title: title || '회의록' }) });
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${title || '회의록'}.${format === 'word' ? 'docx' : 'pdf'}`;
    } catch(e) { alert('다운로드 실패'); }
  };

  // 화상회의 종료 시 녹음 텍스트 받기
  const handleConferenceEnd = (capturedText) => {
    setRawText(capturedText);
    setMode('audio_text');
  };

  const filteredList = savedList.filter(m =>
    !searchTerm || (m.title||'').includes(searchTerm) || (m.attendees||'').includes(searchTerm) || (m.meeting_date||'').includes(searchTerm)
  );

  const mdComponents = {
    table: ({children}) => <table style={{borderCollapse:'collapse',width:'100%',margin:'12px 0',fontSize:14}}>{children}</table>,
    thead: ({children}) => <thead style={{background:TB}}>{children}</thead>,
    th: ({children}) => <th style={{border:'1px solid #b2dfdb',padding:'8px 12px',fontWeight:'bold',textAlign:'left',color:TC}}>{children}</th>,
    td: ({children}) => <td style={{border:'1px solid #e0e0e0',padding:'8px 12px'}}>{children}</td>,
    h1: ({children}) => <h1 style={{color:TC,borderBottom:`2px solid ${TC}`,paddingBottom:8,marginTop:20,fontSize:20}}>{children}</h1>,
    h2: ({children}) => <h2 style={{color:TC,borderLeft:`4px solid ${TC}`,paddingLeft:12,marginTop:16,fontSize:17}}>{children}</h2>,
    h3: ({children}) => <h3 style={{color:'#004d40',marginTop:12,fontSize:15}}>{children}</h3>,
    strong: ({children}) => <strong style={{color:'#004d40'}}>{children}</strong>,
    li: ({children}) => <li style={{marginBottom:4,lineHeight:1.7}}>{children}</li>,
  };

  const RecordingBar = () => (
    <div style={{
      background: 'white', borderRadius: 12, padding: '14px 20px', marginBottom: 16,
      border: isRecording ? '2px solid #ef5350' : '1px solid #e0e0e0',
      boxShadow: isRecording ? '0 0 0 3px rgba(239,83,80,0.15)' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isRecording && !isPaused ? '#ef5350' : TB, animation: isRecording && !isPaused ? 'pulse3 1.5s ease-in-out infinite' : 'none' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="9" y="2" width="6" height="12" rx="3" fill={isRecording && !isPaused ? 'white' : TC}/><path d="M5 11a7 7 0 0014 0" stroke={isRecording && !isPaused ? 'white' : TC} strokeWidth="2" strokeLinecap="round"/><path d="M12 18v4M9 22h6" stroke={isRecording && !isPaused ? 'white' : TC} strokeWidth="2" strokeLinecap="round"/></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 2 }}>{!isRecording ? '음성 녹음' : isPaused ? '일시 정지됨' : '녹음 중...'}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{!isRecording ? '마이크 버튼을 눌러 회의 내용을 녹음하세요' : `${formatTime(recordTime)} 경과`}</div>
        </div>
        {isRecording && !isPaused && interimText && (
          <div style={{ flex: 2, padding: '6px 12px', borderRadius: 8, background: '#fff8e1', border: '1px solid #ffecb3', fontSize: 13, color: '#f57f17', maxHeight: 40, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{interimText}</div>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          {!isRecording ? (
            <button onClick={startRecording} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#ef5350', color: 'white', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" fill="white"/></svg> 녹음 시작
            </button>
          ) : (
            <>
              {!isPaused ? (
                <button onClick={pauseRecording} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ff9800', background: '#fff8e1', color: '#e65100', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>일시정지</button>
              ) : (
                <button onClick={resumeRecording} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #4caf50', background: '#e8f5e9', color: '#2e7d32', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>계속</button>
              )}
              <button onClick={stopRecording} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#333', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1" y="1" width="10" height="10" rx="1" fill="white"/></svg> 녹음 종료
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {showGuide && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '32px 36px', maxWidth: 540,
            width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative',
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#333', marginBottom: 8 }}>
              Chrome 브라우저 설정 안내
            </div>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
              회의 기능(카메라, 마이크, 음성 인식)을 사용하려면 Chrome 보안 설정을 변경해야 합니다.
            </div>
            <div style={{ background: '#f8f9fa', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1565c0', marginBottom: 12 }}>
                Step 1. Chrome 주소창에 아래 URL을 입력하세요
              </div>
              <div
                onClick={() => { const t=document.createElement('textarea');t.value='chrome://flags/#unsafely-treat-insecure-origin-as-secure';document.body.appendChild(t);t.select();document.execCommand('copy');document.body.removeChild(t);alert('복사되었습니다!'); }}
                style={{
                  background: '#1a1a2e', color: '#4fc3f7', padding: '12px 16px', borderRadius: 8,
                  fontSize: 13, fontFamily: 'monospace', cursor: 'pointer', wordBreak: 'break-all',
                  border: '1px solid #333', position: 'relative',
                }}
              >
                chrome://flags/#unsafely-treat-insecure-origin-as-secure
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#aaa', background: '#2a2a3e', padding: '2px 8px', borderRadius: 4 }}>클릭하여 복사</span>
              </div>
            </div>
            <div style={{ background: '#f8f9fa', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1565c0', marginBottom: 12 }}>
                Step 2. "Insecure origins treated as secure" 항목에 추가
              </div>
              <div
                onClick={() => { const t=document.createElement('textarea');t.value='http://ec2-43-202-161-149.ap-northeast-2.compute.amazonaws.com:3000/';document.body.appendChild(t);t.select();document.execCommand('copy');document.body.removeChild(t);alert('복사되었습니다!'); }}
                style={{
                  background: '#1a1a2e', color: '#81c784', padding: '12px 16px', borderRadius: 8,
                  fontSize: 14, fontFamily: 'monospace', cursor: 'pointer',
                  border: '1px solid #333', position: 'relative',
                }}
              >
                http://ec2-43-202-161-149.ap-northeast-2.compute.amazonaws.com:3000/
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#aaa', background: '#2a2a3e', padding: '2px 8px', borderRadius: 4 }}>클릭하여 복사</span>
              </div>
              <div style={{ fontSize: 13, color: '#888', marginTop: 10, lineHeight: 1.5 }}>
                입력 후 드롭다운을 <b>"Enabled"</b>로 변경하고, 하단의 <b>"Relaunch"</b> 버튼을 클릭하여 Chrome을 재시작하세요.
              </div>
            </div>
            <button onClick={dismissGuide} style={{
              width: '100%', padding: '14px 0', border: 'none', borderRadius: 12,
              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(21,101,192,0.3)',
            }}>
              확인했습니다
            </button>
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse3 { 0%,100% { box-shadow: 0 0 0 0 rgba(239,83,80,0.4); } 50% { box-shadow: 0 0 0 10px rgba(239,83,80,0); } }
      `}</style>

      {/* 헤더 */}
      <div style={{ background: 'linear-gradient(135deg, #004d40 0%, #00695c 50%, #00897b 100%)', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', width: 36, height: 36, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M13 4l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>CLSOFTLAB AI SERVICE</div>
          <h1 style={{ color: 'white', fontSize: 20, margin: 0, fontWeight: 700 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{verticalAlign:'middle',marginRight:8}}><rect x="3" y="3" width="18" height="18" rx="3" fill="rgba(255,255,255,0.2)" stroke="white" strokeWidth="1.5"/><path d="M7 8h10M7 12h10M7 16h6" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
            회의록 작성 에이전트
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 3 }}>
          {[
            { key: 'create', label: '새 회의록' },
            { key: 'audio_text', label: '음성/텍스트' },
            { key: 'conference', label: '화상 회의' },
            { key: 'history', label: '저장 목록' },
          ].map(t => (
            <button key={t.key} onClick={() => { if (isRecording) stopRecording(); setMode(t.key); }} style={{
              padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: mode === t.key ? (t.key === 'conference' ? '#ffd54f' : 'white') : 'transparent',
              color: mode === t.key ? (t.key === 'conference' ? '#333' : TC) : 'rgba(255,255,255,0.8)',
            }}>{t.key === 'conference' && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{verticalAlign:'middle',marginRight:4}}>
                <rect x="2" y="4" width="20" height="14" rx="3" stroke={mode === t.key ? '#333' : 'rgba(255,255,255,0.8)'} strokeWidth="2"/>
              </svg>
            )}{t.label}</button>
          ))}
        </div>
      </div>

      {/* 화상 회의 모드 */}
      {mode === 'conference' && (
        <VideoConference onEndCall={handleConferenceEnd} />
      )}

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: mode === 'conference' ? '0' : '20px 20px 60px' }}>
        {/* 히스토리 모드 */}
        {mode === 'history' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="제목, 참석자, 날짜로 검색..."
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            {filteredList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>저장된 회의록이 없습니다.</div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {filteredList.map(m => (
                  <div key={m.id} style={{ background: 'white', borderRadius: 10, padding: '16px 20px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', transition: 'all 0.15s' }}
                    onClick={() => handleLoad(m.id)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = TC; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,105,92,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: TB, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="4" y="3" width="16" height="18" rx="2" stroke={TC} strokeWidth="2"/><path d="M8 8h8M8 12h8M8 16h5" stroke={TC} strokeWidth="1.5" strokeLinecap="round"/></svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: '#1a1a2e', marginBottom: 4 }}>{m.title || '무제'}</div>
                      <div style={{ fontSize: 12, color: '#888', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {m.meeting_date && <span>{m.meeting_date}</span>}
                        {m.location && <span>{m.location}</span>}
                        {m.attendees && <span>{m.attendees.length > 30 ? m.attendees.slice(0,30)+'...' : m.attendees}</span>}
                      </div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); handleDelete(m.id); }}
                      style={{ background: '#ffebee', border: 'none', color: '#c62828', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>삭제</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 새 회의록 / 음성텍스트 모드 */}
        {(mode === 'create' || mode === 'audio_text') && (
          <>
            {result && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ background: 'white', borderRadius: 12, padding: '10px 16px', marginBottom: 12, border: '1px solid #e0e0e0', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: TC, marginRight: 8 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{verticalAlign:'middle',marginRight:6}}><path d="M9 12l2 2 4-4" stroke={TC} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="10" stroke={TC} strokeWidth="2"/></svg>
                    회의록 생성 완료
                  </span>
                  <div style={{ flex: 1 }}/>
                  <button onClick={handleCopy} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="5" y="5" width="9" height="9" rx="1.5" stroke="#555" strokeWidth="1.5"/><path d="M3 11V3h8" stroke="#555" strokeWidth="1.5" strokeLinecap="round"/></svg> 복사
                  </button>
                  <button onClick={() => handleDownload('word')} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #1565c0', background: '#e3f2fd', color: '#1565c0', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Word</button>
                  <button onClick={() => handleDownload('pdf')} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #c62828', background: '#ffebee', color: '#c62828', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>PDF</button>
                  <button onClick={handleSave} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: TC, color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M12 14H4a1 1 0 01-1-1V3a1 1 0 011-1h6l3 3v9a1 1 0 01-1 1z" stroke="white" strokeWidth="1.5"/><path d="M6 14v-4h4v4" stroke="white" strokeWidth="1.2"/></svg> 저장
                  </button>
                  <button onClick={() => setResult('')} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd', background: '#fafafa', cursor: 'pointer', fontSize: 13, color: '#666' }}>새로 작성</button>
                </div>
                <div style={{ background: 'white', borderRadius: 12, padding: '28px 32px', border: '1px solid #e0e0e0', lineHeight: 1.8, fontSize: 14, color: '#333', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', maxHeight: 600, overflowY: 'auto' }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{result}</ReactMarkdown>
                </div>
                <div style={{ background: 'white', borderRadius: 12, padding: '14px 16px', marginTop: 12, border: '1px solid #e0e0e0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>AI 보정</span>
                    {['좀 더 격식체로', '간결하게 줄여줘', 'Action Items 보강', '결정 사항 강조', '영문 병기 추가'].map((q, i) => (
                      <button key={i} onClick={() => setRefineInput(q)} style={{ padding: '4px 12px', borderRadius: 16, border: '1px solid #ddd', background: refineInput === q ? TB : '#fafafa', color: '#555', fontSize: 12, cursor: 'pointer' }}>{q}</button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={refineInput} onChange={e => setRefineInput(e.target.value)} placeholder="수정 요청 (예: 결정 사항을 더 구체적으로)"
                      style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} onKeyDown={e => e.key === 'Enter' && handleRefine()} />
                    <button onClick={handleRefine} disabled={loading} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: loading ? '#aaa' : TC, color: 'white', cursor: loading ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>{loading ? '보정 중...' : '보정'}</button>
                  </div>
                </div>
              </div>
            )}

            {!result && (
              <div style={{ display: 'grid', gridTemplateColumns: mode === 'create' ? '1fr 1fr' : '1fr', gap: 20 }}>
                <div>
                  <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
                    <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#333', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="1" width="12" height="14" rx="2" stroke={TC} strokeWidth="1.5"/><path d="M5 5h6M5 8h6M5 11h4" stroke={TC} strokeWidth="1.2" strokeLinecap="round"/></svg> 회의 정보
                    </h3>
                    <div style={{ display: 'grid', gap: 10 }}>
                      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="회의 제목" style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }} />
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <input type="datetime-local" value={meetingDate} onChange={e => setMeetingDate(e.target.value)} style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }} />
                        <input value={location} onChange={e => setLocation(e.target.value)} placeholder="장소" style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }} />
                      </div>
                      <input value={attendees} onChange={e => setAttendees(e.target.value)} placeholder="참석자 (예: 홍길동, 김철수, 이영희)" style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }} />
                    </div>
                  </div>
                  <RecordingBar />
                  {mode === 'create' && (
                    <>
                      <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>회의 메모 / 핵심 내용</span>
                          {notes && <button onClick={() => setNotes('')} style={{ fontSize: 12, color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}>초기화</button>}
                        </h3>
                        <div style={{ position: 'relative' }}>
                          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={8} placeholder="회의 중 기록한 메모를 입력하거나 녹음 버튼으로 음성 입력하세요..."
                            style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.7 }} />
                          {isRecording && !isPaused && interimText && (
                            <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8, padding: '6px 10px', background: '#fff8e1', borderRadius: 6, fontSize: 13, color: '#f57f17', border: '1px dashed #ffcc02' }}>{interimText}</div>
                          )}
                        </div>
                      </div>
                      <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>작성 스타일</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          {STYLES.map(s => (
                            <button key={s.value} onClick={() => setStyle(s.value)} style={{ padding: '10px 14px', borderRadius: 8, border: `2px solid ${style === s.value ? TC : '#e0e0e0'}`, background: style === s.value ? TB : 'white', cursor: 'pointer', textAlign: 'left' }}>
                              <div style={{ fontWeight: 600, fontSize: 14, color: style === s.value ? TC : '#333' }}>{s.label}</div>
                              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{s.desc}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  {mode === 'audio_text' && (
                    <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>회의 텍스트</span>
                        {rawText && <button onClick={() => setRawText('')} style={{ fontSize: 12, color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}>초기화</button>}
                      </h3>
                      <div style={{ position: 'relative' }}>
                        <textarea value={rawText} onChange={e => setRawText(e.target.value)} rows={14} placeholder="녹음 버튼으로 음성 입력하거나, 텍스트를 직접 붙여넣으세요..."
                          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.7 }} />
                        {isRecording && !isPaused && interimText && (
                          <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8, padding: '6px 10px', background: '#fff8e1', borderRadius: 6, fontSize: 13, color: '#f57f17', border: '1px dashed #ffcc02' }}>{interimText}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {mode === 'create' && (
                  <div>
                    <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>빠른 템플릿</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {QUICK_TEMPLATES.map((t, i) => (
                          <button key={i} onClick={() => setAgenda(t.agenda)} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #e0e0e0', background: 'white', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = TC; e.currentTarget.style.background = TB; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = 'white'; }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 2 }}>{t.label}</div>
                            <div style={{ fontSize: 11, color: '#999', lineHeight: 1.5 }}>{t.agenda.replace(/- /g,'').replace(/\n/g,' / ')}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>안건 / 주제</h3>
                      <textarea value={agenda} onChange={e => setAgenda(e.target.value)} rows={8} placeholder="- 안건 1&#10;- 안건 2&#10;- 안건 3"
                        style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.7 }} />
                    </div>
                  </div>
                )}
                <div style={{ gridColumn: mode === 'create' ? '1 / -1' : '1' }}>
                  <button onClick={handleGenerate} disabled={loading} style={{
                    width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                    background: loading ? '#bbb' : `linear-gradient(135deg, ${TC}, #00897b)`,
                    color: 'white', fontSize: 16, fontWeight: 700, cursor: loading ? 'default' : 'pointer',
                    boxShadow: loading ? 'none' : '0 4px 12px rgba(0,105,92,0.3)',
                  }}>
                    {loading ? (
                      <span><svg width="18" height="18" viewBox="0 0 24 24" style={{verticalAlign:'middle',marginRight:8,animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg>AI가 회의록을 작성하고 있습니다...</span>
                    ) : (mode === 'create' ? '회의록 생성하기' : '텍스트를 회의록으로 변환')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MeetingAgent;
